import { type FC } from 'react';

import { AccoStars } from '@vakantiesnl/components/src/atoms/AccoStars';
import { Avatar } from '@vakantiesnl/components/src/atoms/Avatar';
import { Button } from '@vakantiesnl/components/src/atoms/Button';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import { type Search, type GTM } from '@vakantiesnl/types';

import { useCustomStyles } from './AccommodationBanner.style';
import { isZoover } from '../../utils';

export type AccommodationBannerProps = {
	/** Accommodation data */
	accommodation: Search.CompactAccommodationItem;
	/** Tracking action to dispatch on click */
	onActionClick: (
		label: string,
		atom: string,
		type?: 'action' | 'load',
		action?: GTM.GTMAction,
		shouldOpenModal?: boolean,
		isAccoBannerProvidersBtn?: boolean,
	) => () => void;
	/** Whether to show adlinks */
	hasAdlinks?: boolean;
	/** Action to dispatch on click of the bookable CTA */
	handleOnClickBookableCTA?: (label: string, atom: string, isAccoBannerBookBtn: boolean) => VoidFunction;
	/** Disable the button of the bookable CTA */
	disableCTAButton?: boolean;
};

export const AccommodationBanner: FC<AccommodationBannerProps> = ({
	accommodation,
	hasAdlinks,
	onActionClick,
	handleOnClickBookableCTA,
	disableCTAButton = false,
}) => {
	const { classes: styles } = useCustomStyles();
	const microCopies = useMicroCopyContext();
	const isBookable = accommodation.isBookable || accommodation.isBookableQenner;

	if (!isBookable && !hasAdlinks) return null;

	return (
		<div className={styles.block}>
			<div className={styles.info}>
				<Avatar icon="accommodation" imageURL={accommodation.previewImage} />
				<div className={styles.details}>
					<Typography as="h4" variant="headingMd" className={styles.detailsText}>
						{accommodation.name}
					</Typography>
					<AccoStars className={styles.starRating} starsCount={accommodation.starRating} />
				</div>
			</div>
			<div className={styles.buttons}>
				{!!(isBookable && handleOnClickBookableCTA && onActionClick) && (
					<Button
						onClick={handleOnClickBookableCTA(microCopies['acco.banner.book.button'], 'viewoffercta', true)}
						aria-label={microCopies['acco.banner.book.button']}
						disabled={disableCTAButton}
					>
						{isZoover ? microCopies['common.viewAvailability'] : microCopies['common.bookNow']}
					</Button>
				)}
				{hasAdlinks && !isBookable && (
					<Button
						variant="secondary"
						onClick={onActionClick(
							microCopies['common.toAllAdlinks'],
							'alladlinkscta',
							undefined,
							undefined,
							true,
							true,
						)}
					>
						{microCopies['common.toAllAdlinks']}
					</Button>
				)}
			</div>
		</div>
	);
};
